import {NgModule} from '@angular/core';
import {PreloadAllModules, RouterModule, Routes} from '@angular/router';
import {AuthGuard} from "./providers/auth.guard";
import {AdminAccessGuard} from "./providers/admin-access.guard";
import {UserResolve} from "./resolvers/users.resolve";
import {OrdersResolve} from "./resolvers/orders.resolve";
import {VetReservationsResolve} from "./resolvers/vet-reservations-resolve.service";
import {UsersAccessGuard} from "./providers/users-access.guard";
import {VetReservationsAccessGuard} from "./providers/vet-reservations-access.guard";
import {OrdersAccessGuard} from "./providers/orders-access.guard";
import {ClientsAccessGuard} from "./providers/clients-access.guard";
import {ClientsResolve} from "./resolvers/clients.resolve";
import {LeadsResolve} from "./resolvers/leads.resolve";
import {LeadAccessGuard} from "./providers/lead-access.guard";
import {PartnersAccessGuard} from "./providers/partners-access.guard";
import {PartnersResolve} from "./resolvers/partners.resolve";
import {VetClinicsResolve} from "./resolvers/vet-clinics.resolve";
import {VetReservationResolve} from "./resolvers/vet-reservation-resolve";
import {ReservationAuthGuard} from "./providers/vet-reservation-auth.guard";
import {SettlementsAccessGuard} from "./providers/settlements-access.guard";
import {SummarySettlementsResolve} from "./resolvers/summary-settlements.resolve";
import {OrderSettlementsResolve} from "./resolvers/order-settlements.resolve";
import {PartnerBannersResolve} from "./resolvers/partner-banners.resolve";
import {PartnerBannerActionAccessGuard} from "./providers/partner-banner-actions-access.guard";
import {PartnerBannersActionResolve} from "./resolvers/partner-banner-actions.resolve";
import {VMessagesAccessGuard} from "./providers/v-messages-access.guard";
import {VMessagesResolve} from "./resolvers/v-messages.resolve";
import {TermsOfAgreementAccessGuard} from "./providers/terms-of-agreement-access.guard";
import {VetClinicPetSpeciesAccessGuard} from "./providers/vet-clinic-pet-species-access.guard";
import {VrdResolve} from "./resolvers/vrd.resolve";
import {VrdAuthGuard} from "./providers/vrd-auth.guard";
import {AppUserResolve} from "./resolvers/app-user.resolve";
import {PrepaidAccessGuard} from "./providers/prepaid-access.guard";
import {RefundRequestAccessGuard} from "./providers/refund-request.access.guard";
import {RefundRequestResolve} from "./resolvers/refund-request.resolve";
import {VetineoAccessGuard} from "./providers/vetineo-access.guard";
import {UserResignationResolve} from "./resolvers/user-resignation.resolve";
import {ArticlesAccessGuard} from "./providers/articles-access.guard";
import {ArticlesResolve} from "./resolvers/articles.resolve";
import {MonthlyReportsForClientResolve} from "./resolvers/monthly-reports-for-client.resolve";
import {ClinicsAccessGuard} from "./providers/clinics-access.guard";
import {LabTestDataResolve} from "./resolvers/lab-test-data.resolve";
import {LabTestDataAccessGuard} from "./providers/lab-test-data-access.guard";
import {ReferralsGuard} from "./providers/referrals.guard";
import {ReferralsResolve} from "./resolvers/referrals.resolve";
import {SupportResolve} from "./resolvers/support.resolve";
import {VetDoctorVetVisitTypeResolve} from "./resolvers/vet-doctor-vet-visit-type.resolve";
import {WellnessPlansAccessGuard} from "./providers/wellness-plans-access.guard";
import {WellnessPlansResolve} from "./resolvers/wellness-plans.resolve";
import {UserCarePlanResolve} from "./resolvers/user-care-plan.resolve";
import {CarePlanServiceResolve} from "./resolvers/care-plan-service.resolve";
import {UserCarePlanItemsDetailsResolve} from "./resolvers/user-care-plan-items-details.resolve";
import {UserCarePlanVisitTypeDetailsResolve} from "./resolvers/user-care-plan-visit-type-details.resolve";
import {PartnerProductAccessGuard} from "./providers/partner-product-access.guard";
import {PartnerProductResolve} from "./resolvers/partner-product.resolve";
import {PartnerShopOrdersAccessGuard} from "./providers/partner-shop-orders-access.guard";
import {PartnerShopOrderResolve} from "./resolvers/partner-shop-order.resolve";
import {CarePlanServiceGroupTypesResolve} from "./resolvers/care-plan-service-group-types.resolve";
import {OrderDetailsAuthGuard} from "./providers/order-details-auth.guard";
import {OrderDetailResolve} from "./resolvers/order-detail.resolve";
import {VetineoVisitTypeBundlesAccessGuard} from "./providers/vetineo-visit-type-bundles.access.guard";

const routes: Routes = [
  {
    path: '',
    loadChildren: () => import('./pages/signin/signin.module').then(m => m.SigninPageModule)
  },
  {
    path: 'home',
    loadChildren: () => import('./pages/home/home.module').then(m => m.HomePageModule),
    canLoad: [ AuthGuard ],
    canActivate: [ AuthGuard ]
  },
  {
    path: 'register',
    loadChildren: () => import('./pages/register/register.module').then(m => m.SignupPageModule),
    canLoad: [ AuthGuard, AdminAccessGuard ],
    canActivate: [ AuthGuard, AdminAccessGuard ],
  },
  {
    path: 'signin',
    loadChildren: () => import('./pages/signin/signin.module').then(m => m.SigninPageModule)
  },
  {
    path: 'users',
    loadChildren: () => import('./pages/users/users.module').then(m => m.UsersPageModule),
    canLoad: [ AuthGuard, UsersAccessGuard ],
    canActivate: [ AuthGuard, UsersAccessGuard ],
    resolve: {usersResults: UserResolve}
  },
  {
    path: 'clinic-edit',
    loadChildren: () => import('./pages/clinic-edit/clinic-edit.module').then(m => m.ClinicEditPageModule),
    canLoad: [AuthGuard, ClinicsAccessGuard],
    canActivate: [AuthGuard, ClinicsAccessGuard],
    resolve:{clinicsResults: VetClinicsResolve}
  },
  {
    path: 'clinic-stats',
    loadChildren: () => import('./pages/charts/charts.module').then(m => m.ChartsModule),
    canLoad: [AuthGuard, VetineoAccessGuard],
    canActivate: [AuthGuard, VetineoAccessGuard]
  },
  {
    path: 'twilio-visit',
    loadChildren: () => import('./pages/twilio-visit/twilio-visit.module').then( m => m.TwilioVisitPageModule)
  },
  {
    path: 'vet-reservations',
    loadChildren: () => import('./pages/vet-reservations/vet-reservations.module').then(m => m.VetReservationsPageModule),
    canLoad: [ AuthGuard, VetReservationsAccessGuard ],
    canActivate: [ AuthGuard, VetReservationsAccessGuard ],
    resolve: {vetReservationsResults: VetReservationsResolve}
  },
  {
    path: 'orders',
    loadChildren: () => import('./pages/orders/orders.module').then( m => m.OrdersPageModule),
    canLoad: [ AuthGuard, OrdersAccessGuard ],
    canActivate: [ AuthGuard, OrdersAccessGuard ],
     resolve: {ordersResults: OrdersResolve}
  },
  {
    path: 'daily',
    loadChildren: () => import('./pages/daily-overview/daily-overview.module').then( m => m.DailyOverviewModule),
    canLoad: [ AuthGuard, OrdersAccessGuard ],
    canActivate: [ AuthGuard, OrdersAccessGuard]
  },
  {
    path: 'clients',
    loadChildren: () => import('./pages/clients/clients.module').then( m => m.ClientsPageModule),
    canLoad: [ AuthGuard, ClientsAccessGuard ],
    canActivate: [ AuthGuard, ClientsAccessGuard ],
    resolve: {clientsResults: ClientsResolve }
  },
  {
    path: 'leads',
    loadChildren: () => import('./pages/leads/leads.module').then( m => m.LeadsPageModule),
    canLoad: [ AuthGuard, LeadAccessGuard ],
    canActivate: [ AuthGuard, LeadAccessGuard ],
    resolve: {leadsResults: LeadsResolve }
  },
  {
    path: 'referrals',
    loadChildren: () => import('./pages/referrals/referrals.module').then( m => m.ReferralsPageModule),
    canLoad: [ AuthGuard, ReferralsGuard ],
    canActivate: [ AuthGuard, ReferralsGuard ],
    resolve: {referralResults: ReferralsResolve }
  },
  {
    path: 'account',
    loadChildren: () => import('./pages/account/account.module').then( m => m.AccountPageModule),
  },
  {
    path: 'partners',
    canLoad: [ AuthGuard, PartnersAccessGuard ],
    canActivate: [ AuthGuard, PartnersAccessGuard],
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/partners/partners.module').then( m => m.PartnersPageModule),
        resolve: {partnersResults: PartnersResolve }
      },
      {
        path: 'amb/:param',
        loadChildren: () => import('./pages/partners/partners.module').then( m => m.PartnersPageModule),
        resolve: {partnersResults: PartnersResolve }
      },
    ]
  },
  {
    path: 'partners-banners',
    loadChildren: () => import('./pages/partner-banners/partner-banners.module').then( m => m.PartnerBannersPageModule),
    canLoad: [ AuthGuard, PartnersAccessGuard ],
    canActivate: [ AuthGuard, PartnersAccessGuard],
    resolve: {partnerBannersResults: PartnerBannersResolve }
  },
  {
    path: 'vet-reservation',
    loadChildren: () => import('./pages/vet-reservation/vet-reservation.module').then( m => m.VetReservationPageModule),
    canActivate: [ ReservationAuthGuard, VetReservationsAccessGuard ],
    resolve: {vetReservationResults: VetReservationResolve}
  },
  {
    path: 'settlements',
    loadChildren: () => import('./pages/settlements/settlements.module').then( m => m.SettlementsPageModule),
    canLoad: [ AuthGuard, SettlementsAccessGuard ],
    canActivate: [ AuthGuard, SettlementsAccessGuard],
    resolve: {settlementResults: OrderSettlementsResolve }
  },
  {
    path: 'settlements-report',
    loadChildren: () => import('./pages/settlements-report/settlements-report.module').then( m => m.SettlementsReportPageModule),
    canLoad: [ AuthGuard, SettlementsAccessGuard ],
    canActivate: [ AuthGuard, SettlementsAccessGuard],
    resolve: {summarySettlementsResults: SummarySettlementsResolve }
  },
  {
    path: 'partner-banner-actions',
    loadChildren: () => import('./pages/partner-banner-actions/partner-banner-actions.module').then( m => m.PartnerBannerActionsPageModule),
    canLoad: [ AuthGuard, PartnerBannerActionAccessGuard ],
    canActivate: [ AuthGuard, PartnerBannerActionAccessGuard],
    resolve: {partnerBannersActionResults: PartnerBannersActionResolve }
  },
  {
    path: 'v-messages',
    loadChildren: () => import('./pages/v-messages/v-messages.module').then( m => m.VMessagesPageModule),
    canLoad: [ AuthGuard, VMessagesAccessGuard ],
    canActivate: [ AuthGuard, VMessagesAccessGuard],
    resolve: {vMessagesResults: VMessagesResolve }
  },
  {
    path: 'edit-terms-of-agreement',
    loadChildren: () => import('./pages/edit-terms-of-agreement/edit-terms-of-agreement.module').then( m => m.EditTermsOfAgreementPageModule),
    canLoad: [ AuthGuard, TermsOfAgreementAccessGuard ],
    canActivate: [ AuthGuard, TermsOfAgreementAccessGuard],
  },
  {
    path: 'vet-clinic-pet-species',
    loadChildren: () => import('./pages/vet-clinic-species/vet-clinic-species.module').then( m => m.VetClinicSpeciesPageModule),
    canLoad: [ AuthGuard, VetClinicPetSpeciesAccessGuard ],
    canActivate: [ AuthGuard, VetClinicPetSpeciesAccessGuard],
  },
  {
    path: 'vrd',
    canActivate: [ VrdAuthGuard, VetReservationsAccessGuard ],
    children: [
      {
        path: ':param',
        loadChildren: () => import('./pages/vet-reservation/vet-reservation.module').then( m => m.VetReservationPageModule),
        resolve: {vetReservationResults: VrdResolve},
      },
      {
        path: ':param/files',
        loadChildren: () => import('./pages/vet-reservation/vet-reservation.module').then( m => m.VetReservationPageModule),
        resolve: {vetReservationResults: VrdResolve},
      }
      ]
  },
  {
    path: 'app-users',
    loadChildren: () => import('./pages/app-users/app-users.module').then( m => m.AppUsersPageModule),
    canLoad: [ AuthGuard, UsersAccessGuard ],
    canActivate: [ AuthGuard, UsersAccessGuard ],
    resolve: {usersResults: AppUserResolve}
  },
  {
    path: 'klinikaxp-payment-required-reservations',
    loadChildren: () => import('./pages/klinikaxp-payment-required-reservations/klinikaxp-payment-required-reservations.module').then( m => m.KlinikaxpPaymentRequiredReservationsPageModule),
    canLoad: [ AuthGuard, PrepaidAccessGuard ],
    canActivate: [ AuthGuard, PrepaidAccessGuard ],
    // todo dołożyć, gdy backend będzie gotowy
    //  resolve: {kxpPaymentRequiredReservationsResults: KxpPaymentRequiredReservationResolver}
    },
  {
    path: 'refund-requests',
    loadChildren: () => import('./pages/refund-requests/refund-requests.module').then( m => m.RefundRequestsPageModule),
    canLoad: [ AuthGuard, RefundRequestAccessGuard ],
    canActivate: [ AuthGuard, RefundRequestAccessGuard ],
    resolve: {refundRequestResults: RefundRequestResolve}

  },
  {
    path: 'user-resignations',
    loadChildren: () => import('./pages/user-resignations/user-resignations.module').then( m => m.UserResignationsPageModule),
    canLoad: [ AuthGuard, VetineoAccessGuard ],
    canActivate: [ AuthGuard, VetineoAccessGuard ],
    resolve: {userResignationResults: UserResignationResolve}

  },
  {
    path: 'monthly-report-for-clinic',
    loadChildren: () => import('./pages/monthly-report-for-clinic/monthly-report-for-clinic.module').then( m => m.MonthlyReportForClinicPageModule),
    canLoad: [ AuthGuard, VetineoAccessGuard ],
    canActivate: [ AuthGuard, VetineoAccessGuard ]
  },
  {
    path: 'client-account',
    loadChildren: () => import('./pages/client-account/client-account.module').then( m => m.ClientAccountPageModule),
    canLoad: [ AuthGuard, ClientsAccessGuard ],
    canActivate: [ AuthGuard, ClientsAccessGuard ]
  },
  {
    path: 'monthly-report-for-client',
    loadChildren: () => import('./pages/monthly-report-for-client/monthly-report-for-client.module').then( m => m.MonthlyReportForClientPageModule),
    canLoad: [ AuthGuard, VetineoAccessGuard ],
    canActivate: [ AuthGuard, VetineoAccessGuard ],
    resolve: {monthlyReportsForClient: MonthlyReportsForClientResolve}
  },
  {
    path: 'articles',
    loadChildren: () => import('./pages/articles/articles.module').then( m => m.ArticlesPageModule),
    canLoad: [ AuthGuard, ArticlesAccessGuard ],
    canActivate: [ AuthGuard, ArticlesAccessGuard ],
    resolve: {articlesResults: ArticlesResolve}
  },
  {
    path: 'article-category',
    loadChildren: () => import('./pages/article-category/article-category.module').then( m => m.ArticleCategoryPageModule)
  },
  {
    path: 'edit-information-obligation',
    loadChildren: () => import('./pages/edit-information-obligation/edit-information-obligation.module').then( m => m.EditInformationObligationPageModule)
  },
  {
    path: 'support',
    canActivate: [ VrdAuthGuard, VetReservationsAccessGuard ],
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/support/support.module').then( m => m.SupportPageModule)
      },
      {
        path: ':alias',
        loadChildren: () => import('./pages/support/support.module').then( m => m.SupportPageModule),
        resolve: {articleResults: SupportResolve},
      }
    ]
  },
  {
    path: 'lab-test-data',
    loadChildren: () => import('./pages/lab-test-data/lab-test-data.module').then( m => m.LabTestDataPageModule),
    canLoad: [ AuthGuard, LabTestDataAccessGuard ],
    canActivate: [ AuthGuard, LabTestDataAccessGuard ],
    resolve: {labTestDataResults: LabTestDataResolve}
  },
  {
    path: 'html-content-input',
    loadChildren: () => import('./pages/html-content-input/html-content-input.module').then( m => m.HtmlContentInputPageModule),
    canLoad: [ AuthGuard, VetineoAccessGuard ],
    canActivate: [ AuthGuard, VetineoAccessGuard ],
  },
  {
    path: 'vet-doctor-vet-visit-type',
    loadChildren: () => import('./pages/vet-doctor-vet-visit-type/vet-doctor-vet-visit-type.module').then( m => m.VetDoctorVetVisitTypePageModule),
    canLoad: [AuthGuard, ClinicsAccessGuard],
    canActivate: [AuthGuard, ClinicsAccessGuard],
    resolve:{vetDoctorVetVisitTypes: VetDoctorVetVisitTypeResolve}
  },
  {
    path: 'vetineo-visit-type-bundles',
    loadChildren: () => import('./pages/vetineo-visit-type-bundles/vetineo-visit-type-bundles.module').then( m => m.VetineoVisitTypeBundlesPageModule),
    canLoad: [AuthGuard, VetineoVisitTypeBundlesAccessGuard],
    canActivate: [AuthGuard, VetineoVisitTypeBundlesAccessGuard]
  },
  {
    path: 'wellness-plans',
    loadChildren: () => import('./pages/wellness-plans/wellness-plans.module').then( m => m.WellnessPlansPageModule),
    canLoad: [AuthGuard, WellnessPlansAccessGuard],
    canActivate: [AuthGuard, WellnessPlansAccessGuard],
    resolve:{wellnessPlans: WellnessPlansResolve}
  },
  {
    path: 'care-plan-services',
    loadChildren: () => import('./pages/care-plan-services/care-plan-services.module').then( m => m.CarePlanServicesPageModule),
    canLoad: [AuthGuard, WellnessPlansAccessGuard],
    canActivate: [AuthGuard, WellnessPlansAccessGuard],
    resolve:{carePlanServices: CarePlanServiceResolve}
  },
  {
    path: 'user-care-plans',
    loadChildren: () => import('./pages/user-care-plans/user-care-plans.module').then( m => m.UserCarePlansPageModule),
    canLoad: [AuthGuard, WellnessPlansAccessGuard],
    canActivate: [AuthGuard, WellnessPlansAccessGuard],
    resolve:{userCarePlans: UserCarePlanResolve}
  },
  {
    path: 'user-care-plan-items-details',
    loadChildren: () => import('./pages/user-care-plan-items-details/user-care-plan-items-details.module').then( m => m.UserCarePlanItemsDetailsPageModule),
    canLoad: [AuthGuard, WellnessPlansAccessGuard],
    canActivate: [AuthGuard, WellnessPlansAccessGuard],
    resolve: {userCarePlanItems: UserCarePlanItemsDetailsResolve}
  },
  {
    path: 'user-care-plan-visit-type-details',
    loadChildren: () => import('./pages/user-care-plan-visit-type-details/user-care-plan-visit-type-details.module').then( m => m.UserCarePlanVisitTypeDetailsPageModule),
    canLoad: [AuthGuard, WellnessPlansAccessGuard],
    canActivate: [AuthGuard, WellnessPlansAccessGuard],
    resolve: {userCarePlanVisitTypeDetails: UserCarePlanVisitTypeDetailsResolve}
  },
  {
    path: 'product-recommendations',
    loadChildren: () => import('./pages/product-recommendations/product-recommendations.module').then( m => m.ProductRecommendationsPageModule),
    canLoad: [AuthGuard],
    canActivate: [AuthGuard]
  },
  {
    path: 'partner-products',
    loadChildren: () => import('./pages/partner-products/partner-products.module').then( m => m.PartnerProductsPageModule),
    canLoad: [AuthGuard, PartnerProductAccessGuard],
    canActivate: [AuthGuard, PartnerProductAccessGuard],
    resolve: {partnerProducts: PartnerProductResolve}
  },
  {
    path: 'partner-shop-orders',
    loadChildren: () => import('./pages/partner-shop-orders/partner-shop-orders.module').then( m => m.PartnerShopOrdersPageModule),
    canLoad: [AuthGuard, PartnerShopOrdersAccessGuard],
    canActivate: [AuthGuard, PartnerShopOrdersAccessGuard],
    resolve: {partnerShopOrderResults: PartnerShopOrderResolve}
  },
  {
    path: 'care-plan-service-group-types',
    loadChildren: () => import('./pages/care-plan-service-group-types/care-plan-service-group-types.module').then( m => m.CarePlanServiceGroupTypesPageModule),
    canLoad: [AuthGuard, WellnessPlansAccessGuard],
    canActivate: [AuthGuard, WellnessPlansAccessGuard],
    resolve:{carePlanServicesGroupTypes: CarePlanServiceGroupTypesResolve}
  },
  {
    path: 'order-detail',
    canActivate: [ OrderDetailsAuthGuard,  VetineoAccessGuard],
    children: [
      {
        path: ':param',
        loadChildren: () => import('./pages/order-detail/order-detail.module').then( m => m.OrderDetailPageModule),
        resolve: {orderResult: OrderDetailResolve},
      }]
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule { }
